import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Fish, Anchor, MapPin, Factory, Waves, ShieldCheck, ArrowRight, ExternalLink, FileText, Download, Calendar, Users } from "lucide-react";
import api from "@/lib/api";
import { HOME } from "@/constants/testIds";

const iconMap = { Fish, Anchor, MapPin, Factory, Waves, ShieldCheck };

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" });
  } catch { return ""; }
}

export default function Home() {
  const [stats, setStats] = useState([]);
  const [bidang, setBidang] = useState([]);
  const [berita, setBerita] = useState([]);
  const [galeri, setGaleri] = useState([]);
  const [regulasi, setRegulasi] = useState([]);
  const [mitra, setMitra] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/stats"), api.get("/bidang"), api.get("/berita?limit=3"),
      api.get("/galeri?limit=6"), api.get("/regulasi?limit=5"), api.get("/mitra"),
    ]).then(([s, b, br, g, r, m]) => {
      setStats(s.data); setBidang(b.data); setBerita(br.data);
      setGaleri(g.data); setRegulasi(r.data); setMitra(m.data);
    }).catch(console.error);
  }, []);

  return (
    <div className="w-full">
      {/* HERO */}
      <section className="relative hero-radial min-h-[85vh] overflow-hidden" data-testid="hero-section">
        <img
          src="/assets/silaing/hero.png"
          alt="Perikanan Jawa Barat"
          className="absolute inset-0 w-full h-full object-cover opacity-55"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#003A5C]/95 via-[#003A5C]/70 to-[#003A5C]/20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 lg:py-40">
          <div className="max-w-3xl animate-float-up">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-[#48CAE4] text-xs font-semibold uppercase tracking-widest mb-6">
              <span className="w-2 h-2 rounded-full bg-[#48CAE4] animate-pulse" /> Portal Resmi DKP Provinsi Jawa Barat
            </div>
            <h1 className="font-heading font-bold text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-6">
              Sistem Informasi Laut dan <span className="text-[#48CAE4]">Ikan Terintegrasi</span>
            </h1>
            <p className="text-white/85 text-lg mb-10 max-w-2xl leading-relaxed">
              Data, regulasi, dan layanan resmi seputar kelautan, budidaya, perikanan tangkap, pengolahan, pemasaran, serta pengawasan sumberdaya di Jawa Barat.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/informasi/berita" data-testid={HOME.heroPrimaryCta}
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#00B4D8] hover:bg-[#48CAE4] text-[#003A5C] font-bold transition-colors shadow-lg">
                Jelajahi Data <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/kontak" data-testid={HOME.heroSecondaryCta}
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border-2 border-white/60 hover:border-white text-white hover:bg-white/10 font-bold transition-colors">
                Hubungi Kami
              </Link>
            </div>
          </div>
        </div>
        <div className="wave-bottom" />
      </section>

      {/* QUICK STATS - overlapping */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-20 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {stats.map((s) => {
            const Icon = iconMap[s.icon] || Fish;
            return (
              <div key={s.id} data-testid={HOME.statCard(s.id)} className="lift-card bg-white rounded-2xl p-6 shadow-lg border border-slate-100">
                <div className="w-14 h-14 rounded-full bg-[#E1F5FF] grid place-items-center mb-4">
                  <Icon className="w-7 h-7 text-[#005F8E]" strokeWidth={2} />
                </div>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1">{s.label}</div>
                <div className="font-heading font-bold text-[#003A5C] text-3xl mb-1">{s.value_display}</div>
                <div className="text-sm text-slate-500">{s.satuan}</div>
                <div className="text-[10px] uppercase tracking-wider text-[#0086C6] mt-3 font-semibold">{s.sumber}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* WELCOME */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#0086C6] font-semibold mb-3">Tentang SILAING</div>
            <h2 className="font-heading font-bold text-3xl lg:text-4xl text-[#003A5C] mb-5">Selamat Datang di SILAING</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              SILAING adalah portal informasi resmi Dinas Kelautan dan Perikanan Provinsi Jawa Barat yang menyajikan data terintegrasi seputar kelautan, perikanan budidaya, perikanan tangkap, pengolahan, pemasaran hasil perikanan, serta pengawasan sumberdaya di wilayah Jawa Barat.
            </p>
            <p className="text-slate-600 leading-relaxed">
              Kami berkomitmen menyediakan informasi yang akurat, transparan, dan mudah diakses untuk mendukung pembangunan sektor kelautan dan perikanan yang berkelanjutan.
            </p>
            <div className="mt-6 flex gap-4 text-sm">
              <div className="flex items-center gap-2 text-[#003A5C]"><Users className="w-4 h-4 text-[#00B4D8]" /> 27 Kabupaten/Kota</div>
              <div className="flex items-center gap-2 text-[#003A5C]"><Fish className="w-4 h-4 text-[#00B4D8]" /> 1,6 juta ton produksi</div>
            </div>
          </div>
          <div className="relative">
            <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl border-4 border-white">
              <img src="/assets/silaing/berita-sipelikan-launch.png" alt="Presentasi SILAING oleh Kepala DKP Jawa Barat" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-[#00B4D8] text-white px-6 py-4 rounded-2xl shadow-xl hidden md:block">
              <div className="font-heading font-bold text-lg leading-tight">Data Terintegrasi</div>
              <div className="text-xs uppercase tracking-widest opacity-90 mt-1">Layanan Terpercaya</div>
            </div>
          </div>
        </div>
      </section>

      {/* BIDANG */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="text-xs uppercase tracking-widest text-[#0086C6] font-semibold mb-2">Struktur Organisasi</div>
            <h2 className="font-heading font-bold text-3xl lg:text-4xl text-[#003A5C] mb-3">Bidang Layanan</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">Empat bidang utama Dinas Kelautan dan Perikanan Provinsi Jawa Barat</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {bidang.map((b) => {
              const Icon = iconMap[b.icon] || Fish;
              return (
                <Link key={b.id} to={`/bidang/${b.slug}`} data-testid={HOME.bidangCard(b.slug)}
                  className="lift-card group bg-white rounded-2xl overflow-hidden shadow-md border border-slate-100">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={b.image} alt={b.nama_pendek} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-5 bg-[#005F8E] text-white">
                    <div className="w-11 h-11 rounded-full bg-white/15 grid place-items-center mb-3 -mt-9 border-2 border-white shadow-md">
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="font-heading font-bold text-base leading-tight mb-1">{b.nama_pendek}</div>
                    <div className="text-[11px] uppercase tracking-widest text-[#48CAE4] font-semibold">{b.kode}</div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* BERITA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#0086C6] font-semibold mb-2">Terkini</div>
            <h2 className="font-heading font-bold text-3xl lg:text-4xl text-[#003A5C]">Berita Terbaru</h2>
          </div>
          <Link to="/informasi/berita" className="inline-flex items-center gap-1 text-[#005F8E] hover:text-[#003A5C] font-semibold text-sm">
            Lihat Semua Berita <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {berita.map((b, i) => (
            <Link key={b.id} to={`/informasi/berita/${b.slug}`} data-testid={HOME.beritaCard(i)}
              className="lift-card bg-white rounded-2xl overflow-hidden shadow-md border border-slate-100 flex flex-col">
              <div className="aspect-[16/10] overflow-hidden">
                <img src={b.cover} alt={b.judul} className="w-full h-full object-cover" />
              </div>
              <div className="p-5 flex-1 flex flex-col">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-[#E1F5FF] text-[#005F8E]">{b.kategori}</span>
                  <span className="text-xs text-slate-500">{fmtDate(b.tanggal)}</span>
                </div>
                <h3 className="font-heading font-bold text-[#003A5C] leading-snug mb-2 line-clamp-2">{b.judul}</h3>
                <p className="text-sm text-slate-600 line-clamp-2 mb-4">{b.excerpt}</p>
                <div className="mt-auto inline-flex items-center gap-1 text-[#00B4D8] font-semibold text-sm">Baca Selengkapnya <ArrowRight className="w-3.5 h-3.5" /></div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* GALERI + REGULASI */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12">
          <div>
            <div className="flex items-end justify-between mb-6">
              <div>
                <div className="text-xs uppercase tracking-widest text-[#0086C6] font-semibold mb-2">Dokumentasi</div>
                <h2 className="font-heading font-bold text-2xl lg:text-3xl text-[#003A5C]">Galeri Kegiatan</h2>
              </div>
              <Link to="/informasi/galeri" className="text-sm font-semibold text-[#005F8E] hover:text-[#003A5C] inline-flex items-center gap-1">Semua <ArrowRight className="w-3.5 h-3.5" /></Link>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {galeri.slice(0, 6).map((g) => (
                <Link key={g.id} to="/informasi/galeri" className="lift-card aspect-square rounded-xl overflow-hidden bg-slate-200">
                  <img src={g.image} alt={g.judul} className="w-full h-full object-cover" />
                </Link>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-end justify-between mb-6">
              <div>
                <div className="text-xs uppercase tracking-widest text-[#0086C6] font-semibold mb-2">Legal</div>
                <h2 className="font-heading font-bold text-2xl lg:text-3xl text-[#003A5C]">Regulasi & Dokumen</h2>
              </div>
              <Link to="/informasi/regulasi" className="text-sm font-semibold text-[#005F8E] hover:text-[#003A5C] inline-flex items-center gap-1">Semua <ArrowRight className="w-3.5 h-3.5" /></Link>
            </div>
            <div className="space-y-3">
              {regulasi.slice(0, 5).map((r) => (
                <a key={r.id} href={r.url_dokumen} target="_blank" rel="noopener noreferrer"
                  className="lift-card flex items-start gap-4 p-4 bg-slate-50 rounded-xl border border-slate-100 hover:border-[#00B4D8]">
                  <div className="w-11 h-11 rounded-lg bg-[#E1F5FF] grid place-items-center shrink-0">
                    <FileText className="w-5 h-5 text-[#005F8E]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-[#003A5C] leading-snug line-clamp-2">{r.judul}</div>
                    <div className="text-xs text-slate-500 mt-1">{r.jenis} • {r.tahun}</div>
                  </div>
                  <Download className="w-4 h-4 text-slate-400 shrink-0 mt-2" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SIPELIKAN promo */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-8">
          <div className="text-xs uppercase tracking-widest text-[#0086C6] font-semibold mb-2">Aplikasi Resmi</div>
          <h2 className="font-heading font-bold text-3xl lg:text-4xl text-[#003A5C]">Layanan Digital Terintegrasi</h2>
        </div>
        <div className="relative overflow-hidden rounded-3xl shadow-2xl group">
          <img src="/assets/silaing/sipelikan-banner.png" alt="SIPELIKAN - Sistem Pelaporan dan Pembinaan Mutu Pengolahan Ikan" className="w-full h-auto block" />
          <div className="absolute bottom-6 right-6 md:bottom-10 md:right-10">
            <a href="https://silaing.jabarprov.go.id/sipelikan/" target="_blank" rel="noopener noreferrer" data-testid={HOME.sipelikanCta}
              className="inline-flex items-center gap-2 px-6 py-3 md:px-8 md:py-4 rounded-full bg-[#003A5C] hover:bg-[#005F8E] text-white font-bold text-sm md:text-base transition-colors shadow-2xl border-2 border-white/40">
              Kunjungi SIPELIKAN <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* MITRA */}
      <section className="bg-white py-16 border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="text-xs uppercase tracking-widest text-[#0086C6] font-semibold mb-2">Kolaborasi</div>
            <h2 className="font-heading font-bold text-2xl lg:text-3xl text-[#003A5C]">Mitra &amp; Instansi Terkait</h2>
            <p className="text-slate-600 text-sm mt-2 max-w-2xl mx-auto">Bekerja sama dengan lembaga pemerintah dan akademik untuk menghadirkan data dan layanan kelautan terintegrasi.</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {mitra.map((m, idx) => {
              const logoKey = ["kkp", "jabar", "brin", "ipb", "bps"][idx] || "jabar";
              return (
                <a key={m.id} href={m.url} target="_blank" rel="noopener noreferrer"
                  className="lift-card bg-white rounded-2xl p-6 text-center border border-slate-100 hover:border-[#00B4D8] flex flex-col items-center">
                  <div className="w-20 h-20 grid place-items-center mb-3">
                    <img src={`/assets/silaing/logo-${logoKey}.png`} alt={m.nama} className="max-w-full max-h-full object-contain" />
                  </div>
                  <div className="font-heading font-bold text-sm text-[#003A5C] leading-tight">{m.singkatan}</div>
                  <div className="text-[10px] text-slate-500 mt-1 leading-tight">{m.keterangan}</div>
                </a>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
